import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Collapse, Snackbar, Typography } from '@mui/material';
import { LONGEST_WORDS } from '../data/longestWords';
import { EmojiEvents as TrophyIcon, WhatsApp as WhatsAppIcon, Shuffle as SwapIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

// Mots mis en avant : les mots les plus longs du dictionnaire (donnees dans data/longestWords.js, generees
// depuis le dictionnaire : 13 lettres et plus, sans les tons). Le mot affiche change chaque jour ; le bouton
// « autre mot » passe au suivant. parts : decoupage par repetition (jamais par syllabes, pour ne rien affirmer
// sur la prononciation).
const MAX_LETTERS = Math.max(...LONGEST_WORDS.map((w) => w.letters));
const dayOfYear = () => Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);

const TEXTS = {
  fr: {
    title: 'Les mots les plus longs du dictionnaire',
    letters: (n) => `${n} lettres`,
    another: 'Voir un autre mot long',
    share: 'Partager sur WhatsApp',
    cardLabel: 'Mot record',
    longLabel: 'Mot très long',
    repeated: 'mot répété',
    copied: 'Message copié. Collez-le dans WhatsApp.',
    shareText: (w, meaning, n, url) => `Savez-vous lire l'un des mots les plus longs du dictionnaire duala ?\n\n*${w}* (${n} lettres)\nSens : ${meaning}\n\nEssayez : ${url}`,
  },
  en: {
    title: 'The longest words in the dictionary',
    letters: (n) => `${n} letters`,
    another: 'See another long word',
    share: 'Share on WhatsApp',
    cardLabel: 'Record word',
    longLabel: 'Very long word',
    repeated: 'repeated word',
    copied: 'Message copied. Paste it in WhatsApp.',
    shareText: (w, meaning, n, url) => `Can you read one of the longest words in the Duala dictionary?\n\n*${w}* (${n} letters)\nMeaning: ${meaning}\n\nTry it: ${url}`,
  },
  es: {
    title: 'Las palabras más largas del diccionario',
    letters: (n) => `${n} letras`,
    another: 'Ver otra palabra larga',
    share: 'Compartir en WhatsApp',
    cardLabel: 'Palabra récord',
    longLabel: 'Palabra muy larga',
    repeated: 'palabra repetida',
    copied: 'Mensaje copiado. Péguelo en WhatsApp.',
    shareText: (w, meaning, n, url) => `¿Sabes leer una de las palabras más largas del diccionario duala?\n\n*${w}* (${n} letras)\nSignificado: ${meaning}\n\nPruébalo: ${url}`,
  },
  de: {
    title: 'Die längsten Wörter des Wörterbuchs',
    letters: (n) => `${n} Buchstaben`,
    another: 'Ein anderes langes Wort ansehen',
    share: 'Auf WhatsApp teilen',
    cardLabel: 'Rekordwort',
    longLabel: 'Sehr langes Wort',
    repeated: 'wiederholtes Wort',
    copied: 'Nachricht kopiert. In WhatsApp einfügen.',
    shareText: (w, meaning, n, url) => `Kannst du eines der längsten Wörter des Duala-Wörterbuchs lesen?\n\n*${w}* (${n} Buchstaben)\nBedeutung: ${meaning}\n\nProbier es: ${url}`,
  },
};

const LETTER_DELAY_MS = 70;

function LongestWordCard({ language = 'fr' }) {
  const t = TEXTS[language] || TEXTS.fr;
  const [index, setIndex] = useState(() => dayOfYear() % LONGEST_WORDS.length);
  const [notice, setNotice] = useState('');
  const [open, setOpen] = useState(false);
  const openedAtScroll = useRef(0);

  // Replie la carte des que la page defile (le lecteur reprend sa lecture) : seuil de 40 px
  // pour ne pas se fermer au moindre effleurement ni a cause du decalage causé par l'ouverture.
  useEffect(() => {
    if (!open) return undefined;
    openedAtScroll.current = window.scrollY;
    const onScroll = () => {
      if (Math.abs(window.scrollY - openedAtScroll.current) > 40) setOpen(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [open]);
  const entry = LONGEST_WORDS[index];
  const meaning = entry[language] || entry.fr;
  const count = entry.letters;
  const isRecord = count === MAX_LETTERS;
  const isRepeated = entry.parts.length > 1;

  const handleShare = async () => {
    const message = t.shareText(entry.word, meaning, count, window.location.href.split('#')[0]);
    const link = `https://wa.me/?text=${encodeURIComponent(message)}`;
    if (link.length <= 1800) {
      window.open(link, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      await navigator.clipboard.writeText(message);
      setNotice(t.copied);
    } catch {
      setNotice(message);
    }
    window.open('https://wa.me/', '_blank', 'noopener,noreferrer');
  };

  let letterIndex = 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Button
        size="small"
        variant="outlined"
        color="warning"
        startIcon={<TrophyIcon />}
        endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 3 }}
      >
        {t.title}
      </Button>
      <Collapse in={open} unmountOnExit timeout={250}>
    <Box
      sx={{
        mt: 1.5,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'warning.light',
        background: 'linear-gradient(135deg, rgba(255,193,7,0.14), rgba(255,87,34,0.08))',
        '@keyframes lwPop': {
          '0%': { opacity: 0, transform: 'translateY(8px) scale(0.6)' },
          '70%': { opacity: 1, transform: 'translateY(-2px) scale(1.1)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        '@media (prefers-reduced-motion: reduce)': { '& .lw-letter': { animation: 'none !important', opacity: 1 } },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Chip size="small" color="warning" label={`${isRecord ? t.cardLabel : t.longLabel} · ${t.letters(count)}`} sx={{ fontWeight: 600 }} />
        {isRepeated && <Chip size="small" variant="outlined" label={t.repeated} />}
      </Box>

      {/* key = index : l'animation rejoue a chaque changement de mot */}
      <Box key={index} role="img" aria-label={entry.word} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0, rowGap: 0.5, my: 1 }}>
        {entry.parts.map((part, pi) => (
          <Box key={`${part}-${pi}`} component="span" sx={{ display: 'inline-flex', color: ['#c2410c', '#0f766e', '#6d28d9'][pi % 3] }}>
            {[...part].map((ch) => {
              const delay = letterIndex++ * LETTER_DELAY_MS;
              return (
                <Box
                  key={delay}
                  component="span"
                  className="lw-letter"
                  sx={{
                    fontSize: { xs: '1.6rem', sm: '2.2rem' },
                    fontWeight: 800,
                    letterSpacing: '0.02em',
                    opacity: 0,
                    animation: `lwPop 380ms ${delay}ms ease-out forwards`,
                  }}
                >
                  {ch}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Typography variant="body2" sx={{ mb: 0.5 }}>
        « {meaning} »{entry.example ? <Box component="span" sx={{ color: 'text.secondary' }}> — <em>{entry.example}</em></Box> : null}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<WhatsAppIcon />}
          onClick={handleShare}
          sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' } }}
        >
          {t.share}
        </Button>
        <Button
          size="small"
          startIcon={<SwapIcon />}
          onClick={() => setIndex((i) => (i + 1) % LONGEST_WORDS.length)}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {t.another}
        </Button>
      </Box>

    </Box>
      </Collapse>
      <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice('')} message={notice} />
    </Box>
  );
}

export default LongestWordCard;
